import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import {
  ConversationFilterNames,
  conversationFilterData,
} from "@redotech/effect-api-shared/schema/conversation-filters";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { ArrayStringFilterOperator } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { FilterOperator } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import {
  type NumberFilterData,
  NumberFilterOperator,
} from "@redotech/redo-model/views/advanced-filters/number-filter";
import type { FilterGroup } from "@redotech/redo-model/views/views";

export enum DefaultView {
  OPEN = "open",
  ASSIGNED = "assigned",
  SENT = "sent",
  DRAFTS = "drafts",
  SPAM = "spam",
  ARCHIVED = "archived",
}

export const DEFAULT_VIEW_DISPLAY_NAMES: Record<DefaultView, string> = {
  [DefaultView.OPEN]: "Open",
  [DefaultView.ASSIGNED]: "Assigned to me",
  [DefaultView.SENT]: "Sent",
  [DefaultView.DRAFTS]: "Drafts",
  [DefaultView.SPAM]: "Spam",
  [DefaultView.ARCHIVED]: "Archived",
};

const openStatusFilter: ArrayStringFilterData = {
  ...(conversationFilterData[
    ConversationFilterNames.STATUS
  ] as ArrayStringFilterData),
  value: ["OPEN"],
  operator: ArrayStringFilterOperator.ANY_OF,
};

export const OPEN_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [openStatusFilter],
};

export function buildAssignedViewFilterGroup(userId: string): FilterGroup {
  const assigneesFilter: ArrayStringFilterData = {
    ...(conversationFilterData[
      ConversationFilterNames.ASSIGNEES
    ] as ArrayStringFilterData),
    value: [MongoUserId.fromString(userId)],
    operator: ArrayStringFilterOperator.ANY_OF,
  };
  return {
    operator: FilterOperator.AND,
    clauses: [openStatusFilter, assigneesFilter],
  };
}

const numSentMessagesFilter: NumberFilterData = {
  ...(conversationFilterData[
    ConversationFilterNames.NUM_SENT_MESSAGES
  ] as NumberFilterData),
  value: [0],
  operator: NumberFilterOperator.GREATER_THAN,
};

export const SENT_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [numSentMessagesFilter],
};

const numDraftsFilter: NumberFilterData = {
  ...(conversationFilterData[
    ConversationFilterNames.NUM_DRAFTS
  ] as NumberFilterData),
  value: [0],
  operator: NumberFilterOperator.GREATER_THAN,
};

export const DRAFTS_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [numDraftsFilter],
};

export const SPAM_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [
    {
      ...(conversationFilterData[
        ConversationFilterNames.CONVERSATION_TAGS
      ] as ArrayStringFilterData),
      value: ["spam"],
      operator: ArrayStringFilterOperator.ANY_OF,
    },
  ],
};

export const ARCHIVED_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [
    {
      ...(conversationFilterData[
        ConversationFilterNames.STATUS
      ] as ArrayStringFilterData),
      value: ["ARCHIVED"],
      operator: ArrayStringFilterOperator.ANY_OF,
    },
  ],
};

export function isDefaultView(value: string): value is DefaultView {
  return Object.values(DefaultView).includes(value as DefaultView);
}
